import { type TFilterWithViewAllRef } from '@/components';
import { MasterInfoEnum, OpenFromEnum } from '@/constant';
import { useCommonList, useRowSelection, useSearchFilterRef } from '@/hooks';
import { ISelectApplicantPopup } from '@/services';
import { insertObjectIfV2, showAlert } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TableRowSelection } from 'antd/es/table/interface';

import { pick } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';

import { getColumns, initialValues } from './select-applicant-modal.config';
import { DataType, IProps } from './select-applicant-modal.type';

export const useLogic = (props: IProps) => {
	const { openFrom, open, onSaveApplicant, program, onCloseModal, id } = props;
	const filterRef = useRef<TFilterWithViewAllRef>(null);

	const rowSelection = useRowSelection<
		TableRowSelection<ISelectApplicantPopup>,
		ISelectApplicantPopup
	>({
		isSingle: openFrom === OpenFromEnum.BM1_03,
		hideSelectAll: openFrom === OpenFromEnum.BM1_03,
	});

	const [dataSource, setDataSource] = useState<DataType>([]);
	const relationshipRefs = useRef<Record<number, string>>({});

	useEffect(() => {
		if (!open) {
			filterRef?.current?.onReset?.();
			setParams((prev) => ({
				...prev,
				...initialValues,
			}));
		}
	}, [open, filterRef]);

	const [params, setParams] = useState({
		...initialValues,
		programId: program?.id,
		id: id,
		franchiseId: id,
	});

	const fetchData = useCallback((params: any) => {
		setParams((prev) => ({
			...prev,
			...params,
		}));
	}, []);

	const renderMasterInfo = useCallback(() => {
		switch (openFrom) {
			case OpenFromEnum.PM1_03:
				return MasterInfoEnum.APPLICANT_BY_PROGRAM;
			case OpenFromEnum.RM1_03:
				return MasterInfoEnum.APPLICANT_BY_RECOMMENDER;
			case OpenFromEnum.LM1_03:
				return MasterInfoEnum.APPLICANT_BY_LODGING;
			default:
				return MasterInfoEnum.APPLICANT_BY_FRANCHISE;
		}
	}, [openFrom]);

	const renderParams = useCallback(() => {
		switch (openFrom) {
			case OpenFromEnum.PM1_03:
				return params;
			case OpenFromEnum.RM1_03:
				return pick(['id', 'applicantName'], params);
			case OpenFromEnum.LM1_03:
				return pick(['applicantName'], params);
			default:
				return pick(['franchiseId'], params);
		}
	}, [openFrom, params]);

	const {
		commonList: { fetching, data },
		fetchData: fetchApplicant,
	} = useCommonList<ISelectApplicantPopup>(
		renderMasterInfo(),
		open,
		renderParams()
	);

	const handleSave = useCallback((row: DataType[number]) => {
		setDataSource((prev) => [...prev, row]);
	}, []);

	const columns = useMemo(
		() =>
			getColumns({ openFrom, relationshipRefs }).map((col) => {
				if (!col.editable) {
					return col;
				}
				return {
					...col,
					onCell: (record: DataType) => ({
						record,
						editable: col.editable,
						dataIndex: col?.dataIndex,
						title: col.title,
						cellType: col.cellType,
						inputProps: col.inputProps,
						dateProps: col.dateProps,
						dropdownProps: col.dropdownProps,
						handleSave,
					}),
				};
			}),
		[openFrom]
	);

	const onSaveClick = useCallback(() => {
		const keys = rowSelection.selectedRowKeys?.map(Number);
		const values = rowSelection.selectedRows?.map((item) => ({
			...item,
			...insertObjectIfV2(props.openFrom === OpenFromEnum.LM1_03, {
				lodgingRelationship: relationshipRefs.current[item.id],
			}),
			...insertObjectIfV2(props.openFrom === OpenFromEnum.RM1_03, {
				recommenderRelationship: relationshipRefs.current[item.id],
			}),
		}));

		if (isNilOrEmpty(values)) {
			showAlert('근로자를(을) 선택해주세요.');
			return;
		}

		onSaveApplicant?.(keys, values);

		onCloseModal();
	}, [dataSource, rowSelection, onSaveApplicant, onCloseModal]);

	const searchFilterRef = useSearchFilterRef({
		onSearchChange: fetchApplicant,
	});

	if (searchFilterRef.current) {
		searchFilterRef.current.isSearching = isNotNilOrEmpty(
			params?.applicantName
		);
	}

	return {
		filterRef,
		initialValues,
		fetchData,
		fetching,
		columns,
		dataSource: data,
		rowSelection,
		onSaveClick,
		searchFilterRef,
	};
};
